import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Text,
  Stack,
  Select,
  FormControl,
  FormLabel,
  Flex,
  IconButton,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Spinner,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon, AddIcon } from "@chakra-ui/icons";
import { ObservationColumnProto } from "build-ts/metadata";
import { ColumnType, MetadataTypeEnum } from "build-ts/enums";
import { RecommendationProto, SelectionType } from "build-ts/analysis_plan";
import ColumnValues from "components/common/ColumnValues";
import { getKGColumnValues } from "services/DataModelService";
import { RecommendationRuleProto, SelectionCriteriaProto } from "build-ts/analysis_plan";
import { ColumnHashProto } from "build-ts/data";
import { useKGAnalysisPlanContext } from "context/batch/KGAnalysisPlanContext";
import { GetRecommendations_Request } from "build-ts/interface";
import { getRecommendationEdits } from "services/AnalysisTreeServices";
import { KGStepInterface } from "./Interface";
import { getAnalysisColumnTypeDisplay } from "utils/nameUtils";
import showToast from "hooks/useCustomToast";

interface RecommendationProps {
  currentNode: KGStepInterface;
  addChildToRecommendation: (recommendationIndex: number) => void;
}

interface AttributeSelectionState {
  column: ObservationColumnProto | null;
  selectedValues: string[];
  attributeValues: string[];
  isLoading: boolean;
}

// New interface for tracking added attribute columns and their values
interface AttributeColumnValue {
  column: ObservationColumnProto;
  values: string[];
}

// New interface for column hash display option
interface ColumnHashOption {
  label: string;
  columnHash: ColumnHashProto;
}

const KGRecommendations: React.FC<RecommendationProps> = ({
  currentNode,
  addChildToRecommendation,
}) => {

  const { 
    deleteChildrenOfRecommendation,
    getKGObservationFromKGStepInterface,
    updateNode,
    selectedNodeId
  } = useKGAnalysisPlanContext();
  // State for new recommendation
  const [selectionType, setSelectionType] = useState<SelectionType | number>(SelectionType.NONE);
  
  // State for current attribute selection
  const [attributeSelection, setAttributeSelection] = useState<AttributeSelectionState>({
    column: null,
    selectedValues: [],
    attributeValues: [],
    isLoading: false,
  });
  
  // State for already added attribute columns and values
  const [addedAttributes, setAddedAttributes] = useState<AttributeColumnValue[]>([]);
  
  // State for column selection (for SELECT_METRIC_MEASURES)
  const [selectedColumns, setSelectedColumns] = useState<ObservationColumnProto[]>([]);
  
  // State for TopN and BottomN selection
  const [nValue, setNValue] = useState<number>(5);
  const [selectedColumnHashes, setSelectedColumnHashes] = useState<ColumnHashProto[]>([]);
  
  // Add loading state for recommendations
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState<boolean>(false);
  // Add loading state for child node creation
  const [loadingChildIndex, setLoadingChildIndex] = useState<number | null>(null);
  
  // Update recommendations when current node changes
  useEffect(() => {
    
    // Reset all selection states when node changes
    setSelectionType(SelectionType.NONE);
    setAttributeSelection({
      column: null,
      selectedValues: [],
      attributeValues: [],
      isLoading: false,
    });
    setAddedAttributes([]);
    setSelectedColumns([]);
    setSelectedColumnHashes([]);
    setNValue(50);
  }, [currentNode, selectedNodeId]);
  
  // Filter attribute columns
  const attributeColumns = currentNode?.selected_columns?.filter(col => 
    col.kg_column_header?.column_type === ColumnType.ATTRIBUTE);
  
  // Filter non-attribute columns (for metrics and measures)
  const nonAttributeColumns = useMemo(() => {
    const columns: ObservationColumnProto[] = [];
    
    // Add columns that aren't attributes
    columns.push(
      ...(currentNode?.selected_columns?.filter(col => 
        col.kg_column_header?.column_type !== ColumnType.ATTRIBUTE 
        && col.kg_column_header?.type !== MetadataTypeEnum.METRIC
        && col.column_id
      ) || [])
    );
    
    // Add metrics that aren't attributes
    currentNode?.selected_metrics?.forEach(metric => {
      if (metric?.header && metric.header.column_type !== ColumnType.ATTRIBUTE) {
        columns.push(ObservationColumnProto.create({
          column_id: metric?.header?.id || "",
          kg_column_header: metric?.header,
        }));
      }
    });
    
    return columns;
  }, [currentNode?.selected_columns, currentNode?.selected_metrics]);

  // Create column map for easy lookup
  const columnMap = useMemo(() => {
    const map = new Map<string, ObservationColumnProto>();
    
    // Add columns passed to the component
    currentNode?.selected_columns?.forEach(col => {
      if (col.column_id) {
        map.set(col.column_id, col);
      }
    });
    
    // Also add all columns from the current node's non_benchmarks to ensure complete coverage
    currentNode?.non_benchmarks?.forEach(col => {
      if (col.column_id) {
        map.set(col.column_id, col);
      }
    });
    
    // Add attribute columns as well
    currentNode?.selected_columns?.filter(col => col?.kg_column_header?.column_type === ColumnType.ATTRIBUTE).forEach(col => {
      if (col.column_id) {
        map.set(col.column_id, col);
      }
    });
    
    return map;
  }, [currentNode?.selected_columns, currentNode?.selected_metrics, currentNode?.selected_benchmarks]);
  
  // Helper function to find a column name by ID searching only in the current node
  const findColumnNameById = useMemo(() => {
    return (id: string): string | null => {
      if (!currentNode || !id) return null;
      
      // Check in column map first (includes current selected columns)
      const column = columnMap.get(id);
      if (column?.kg_column_header?.name) {
        return column.kg_column_header.name;
      }
      
      // Check columns passed directly to the component
      const matchingProvidedColumn = currentNode?.selected_columns?.find(col => 
        col.column_id === id || col.kg_column_header?.id === id);
      if (matchingProvidedColumn?.kg_column_header?.name) {
        return matchingProvidedColumn.kg_column_header.name;
      }
      
      // Check in available metrics
      const matchingMetric = currentNode.metrics?.find(m => m.header?.id === id);
      if (matchingMetric?.header?.name) {
        return matchingMetric.header.name;
      }
      
      // Check in selected metrics
      const matchingSelectedMetric = currentNode.selected_metrics?.find(m => m.header?.id === id);
      if (matchingSelectedMetric?.header?.name) {
        return matchingSelectedMetric.header.name;
      }
      
      // Check in non_benchmarks (available columns)
      const matchingNonBenchmark = currentNode.non_benchmarks?.find(col => 
        col.column_id === id || col.kg_column_header?.id === id);
      if (matchingNonBenchmark?.kg_column_header?.name) {
        return matchingNonBenchmark.kg_column_header.name;
      }
      
      // Check in benchmarks
      const matchingBenchmark = currentNode.selected_benchmarks?.find(b => b.header?.id === id);
      if (matchingBenchmark?.header?.name) {
        return matchingBenchmark.header.name;
      }
      
      // If not found, return null (we'll filter these out)
      return null;
    };
  }, [currentNode, columnMap, selectedNodeId]);
  
  // Generate column hash options from analysis_columns - only include ones we can resolve names for
  const columnHashOptions = useMemo(() => {
    // Map to track unique options to avoid duplicates
    const uniqueOptions = new Map<string, ColumnHashOption>();
    
    // If no node is selected, return empty array
    if (!currentNode) return [];
    

    // Add actual+benchmark combination for selected metrics.
    currentNode.selected_metrics?.forEach(metric => {
      const actualsId = metric.header?.id || "";
      const actualsName = metric.header?.name || "";
      const actualKey = `actual:${actualsId}`;
      if (!uniqueOptions.has(actualKey)) {
        uniqueOptions.set(actualKey, {
          label: actualsName,
          columnHash: ColumnHashProto.create({ actuals_id: actualsId })
        });
      }
      currentNode.selected_benchmarks?.forEach((benchmark) => {
        const benchmarkId = benchmark.header?.id || "";
        const benchmarkName = benchmark.header?.name || "";
        // Add actual+benchmark combination
        const combinedKey = `combined:${actualsId}:${benchmarkId}`;
        if (!uniqueOptions.has(combinedKey)) {
          uniqueOptions.set(combinedKey, {
            label: `${benchmarkName} of ${actualsName}`,
            columnHash: ColumnHashProto.create({
              actuals_id: actualsId,
              benchmark_id: benchmarkId,
            })
          });
        }
      });
    });
    // Add actual+benchmark combination for selected columns.
    currentNode?.selected_columns?.forEach(column => {
      if (column.kg_column_header?.column_type === ColumnType.ATTRIBUTE) return;
      const actualsId = column.column_id || "";
      const actualsName = column.kg_column_header?.name || "";
      const actualKey = `actual:${actualsId}`;
      if (!uniqueOptions.has(actualKey)) {
        uniqueOptions.set(actualKey, {
          label: actualsName,
          columnHash: ColumnHashProto.create({ actuals_id: actualsId })
        });
      }
      currentNode.selected_benchmarks?.forEach((benchmark) => {
        const benchmarkId = benchmark.header?.id || "";
        const benchmarkName = benchmark.header?.name || ""; 
        const combinedKey = `combined:${actualsId}:${benchmarkId}`;
        if (!uniqueOptions.has(combinedKey)) {
          uniqueOptions.set(combinedKey, {
            label: `${benchmarkName} of ${actualsName}`,
            columnHash: ColumnHashProto.create({
              actuals_id: actualsId,
              benchmark_id: benchmarkId,
            })
          });
        }
      });
    });
    // Process all analysis columns for column type recommendations.
    currentNode.analysis_columns?.forEach(columnHash => {
      const actualsId = columnHash.actuals_id || "";
      const benchmarkId = columnHash.benchmark_id || "";
      const columnType = columnHash.analysis_column_type || "";
      
      // Skip invalid entries
      if (!actualsId) return;
      
      // Only add options if we can resolve the column name
      const actualName = findColumnNameById(actualsId);
      if (!actualName) return; // Skip if we can't find the column name
      
      // Skip if this is an ATTRIBUTE column
      // Check if this is an attribute column by looking up in the node's non_benchmarks
      const matchingColumn = currentNode.non_benchmarks?.find(col => 
        col.column_id === actualsId && 
        col.kg_column_header?.column_type === ColumnType.ATTRIBUTE
      );
      if (matchingColumn) return; // Skip attribute columns
      
      // 1. Add option for just the actual column (if not already added)
      const actualKey = `actual:${actualsId}`;
      if (!uniqueOptions.has(actualKey)) {
        uniqueOptions.set(actualKey, {
          label: actualName,
          columnHash: ColumnHashProto.create({ actuals_id: actualsId })
        });
      }
      
      // 2. Add option for actual+benchmark combination (if benchmark exists)
      if (benchmarkId) {
        const benchmarkName = findColumnNameById(benchmarkId);
        // Only add if we can resolve the benchmark name
        if (benchmarkName) {
          // Add actual+benchmark combination
          const combinedKey = `combined:${actualsId}:${benchmarkId}`;
          if (!uniqueOptions.has(combinedKey)) {
            uniqueOptions.set(combinedKey, {
              label: `${benchmarkName} of ${actualName}`,
              columnHash: ColumnHashProto.create({ 
                actuals_id: actualsId,
                benchmark_id: benchmarkId 
              })
            });
          }
          
          // 3. Add option for each analysis type available in the node
          if (columnType) {
            const varianceKey = `variance:${actualsId}:${benchmarkId}:${columnType}`;
            const analysisTypeDisplay = getAnalysisColumnTypeDisplay(columnType);
            uniqueOptions.set(varianceKey, {
              label: `${analysisTypeDisplay} of ${benchmarkName} of ${actualName}`,
              columnHash: ColumnHashProto.create({ 
                actuals_id: actualsId,
                benchmark_id: benchmarkId,
                analysis_column_type: columnType
              })
            });
          }
          
          // 4. Also look for all other analysis types used with this benchmark anywhere in the node
          currentNode.analysis_columns?.forEach(otherHash => {
            if (otherHash.benchmark_id === benchmarkId && 
                otherHash.analysis_column_type && 
                otherHash.analysis_column_type !== columnType) {
              // Create an option for this combination if it doesn't exist yet
              const otherKey = `variance:${actualsId}:${benchmarkId}:${otherHash.analysis_column_type}`;
              
              if (!uniqueOptions.has(otherKey)) {
                const analysisTypeDisplay = getAnalysisColumnTypeDisplay(otherHash.analysis_column_type);
                uniqueOptions.set(otherKey, {
                  label: `${analysisTypeDisplay} of ${benchmarkName} of ${actualName}`,
                  columnHash: ColumnHashProto.create({ 
                    actuals_id: actualsId,
                    benchmark_id: benchmarkId,
                    analysis_column_type: otherHash.analysis_column_type
                  })
                });
              }
            }
          });
        }
        }
    });
    // Convert map to array for rendering
    return Array.from(uniqueOptions.values());
  }, [currentNode, findColumnNameById, currentNode?.analysis_columns, selectedNodeId]);
  
  // Reset states when selection type changes
  useEffect(() => {
    setAttributeSelection({
      column: null,
      selectedValues: [],
      attributeValues: [],
      isLoading: false,
    });
    setAddedAttributes([]);
    setSelectedColumns([]);
    setSelectedColumnHashes([]);
    setNValue(50);
  }, [selectionType]);
  
  // Fetch attribute values when column is selected
  useEffect(() => {
    if (attributeSelection.column && attributeSelection?.column?.kg_column_header?.id) {
      const fetchValues = async () => {
        setAttributeSelection(prev => ({ ...prev, isLoading: true }));
        try {
          const values = await getKGColumnValues(attributeSelection.column?.kg_column_header?.id || "");
          setAttributeSelection(prev => ({
            ...prev,
            attributeValues: values || [],
            isLoading: false,
          }));
        } catch (error) {
          console.error("Error fetching attribute values:", error);
          setAttributeSelection(prev => ({
            ...prev,
            attributeValues: [],
            isLoading: false,
          }));
        }
      };
      
      fetchValues();
    }
  }, [attributeSelection.column]);
  
  // Handle attribute column selection
  const handleAttributeColumnSelect = (column: ObservationColumnProto) => {
    setAttributeSelection({
      column,
      selectedValues: [],
      attributeValues: [],
      isLoading: true,
    });
  };
  
  // Handle attribute values selection
  const handleAttributeValuesChange = (values: string[]) => {
    setAttributeSelection(prev => ({
      ...prev,
      selectedValues: values,
    }));
  };
  
  // Add current attribute selection to added attributes
  const addAttributeToSelection = () => {
    if (!attributeSelection.column || attributeSelection.selectedValues.length === 0) return;
    
    // Add current selection to added attributes
    setAddedAttributes(prev => [
      ...prev,
      {
        column: attributeSelection.column!,
        values: attributeSelection.selectedValues,
      }
    ]);
    
    // Reset the attribute selection for next attribute
    setAttributeSelection({
      column: null,
      selectedValues: [],
      attributeValues: [],
      isLoading: false,
    });
  };
  
  // Remove an added attribute
  const removeAttributeFromSelection = (index: number) => {
    setAddedAttributes(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };
  
  // Toggle selection of a non-attribute column
  const toggleColumnSelection = (column: ObservationColumnProto) => {
    setSelectedColumns(prev => {
      const isSelected = prev.some(c => c.column_id === column.column_id);
      if (isSelected) {
        return prev.filter(c => c.column_id !== column.column_id);
      } else {
        return [...prev, column];
      }
    });
  };
  
  // Toggle selection of a column hash
  const toggleColumnHashSelection = (columnHash: ColumnHashProto) => {
    setSelectedColumnHashes(prev => {
      // Check if already selected by comparing actuals_id and benchmark_id
      const isSelected = prev.some(ch => 
        ch.actuals_id === columnHash.actuals_id && 
        ch.benchmark_id === columnHash.benchmark_id &&
        ch.analysis_column_type === columnHash.analysis_column_type
      );
      
      if (isSelected) {
        return prev.filter(ch => 
          !(ch.actuals_id === columnHash.actuals_id && 
            ch.benchmark_id === columnHash.benchmark_id &&
            ch.analysis_column_type === columnHash.analysis_column_type)
        );
      } else {
        // When adding a column hash, also check if this is a benchmark with analysis types
        const result = [...prev, columnHash];
        
        // If this is a basic hash (no analysis_column_type), check if we should add analysis types
        if (!columnHash.analysis_column_type && columnHash.benchmark_id) {
          // Find existing analysis types used with this benchmark in the current node
          currentNode?.analysis_columns?.forEach(existingHash => {
            if (existingHash.benchmark_id === columnHash.benchmark_id && 
                existingHash.analysis_column_type) {
              // Check if we already have this combination
              const alreadyHave = result.some(ch =>
                ch.actuals_id === columnHash.actuals_id &&
                ch.benchmark_id === columnHash.benchmark_id &&
                ch.analysis_column_type === existingHash.analysis_column_type
              );
              
              // If not, add it
              if (!alreadyHave) {
                result.push(ColumnHashProto.create({
                  actuals_id: columnHash.actuals_id,
                  benchmark_id: columnHash.benchmark_id,
                  analysis_column_type: existingHash.analysis_column_type
                }));
              }
            }
          });
        }
        
        return result;
      }
    });
  };
  
  // Add recommendation and then add a child node 
  const handleAddRecommendationAndChild = async (index: number) => {
    if (!currentNode) return;
    
    // Set loading state for this specific recommendation
    setLoadingChildIndex(index);
    // Get current KGObservation from the node
    const kgObservation = getKGObservationFromKGStepInterface(currentNode?.id);
    if (!kgObservation) {
      showToast("Cannot add child: KGObservation not available", "", "error");
      setLoadingChildIndex(null);
      return;
    }
    // Call the parent component's addChildToRecommendation function
    await addChildToRecommendation(index);
    // Reset loading state
    setLoadingChildIndex(null);
  };
  
  // Add attribute selections to create a new recommendation
  const addAttributeSelection = async() => {
    // If there are no attributes in added attributes or in current selection, return
    if (addedAttributes.length === 0 
          && (!attributeSelection.column 
                    || attributeSelection.selectedValues.length === 0)) return;
    
    setIsLoadingRecommendation(true);
    
    try {
      // If there's a current selection that hasn't been added yet, add it first
      if (attributeSelection.column && attributeSelection.selectedValues.length > 0) {
        addAttributeToSelection();
      }
      // Create attribute_values_map from all added attributes
      const attributeValuesMap = addedAttributes.map(attr => ({
        column_id: attr.column?.column_id || "",
        values: attr.values,
      }));
      
      // Create selection criteria
      const selectionCriterias: SelectionCriteriaProto[] = [];
      selectionCriterias.push(SelectionCriteriaProto.create({
        type: SelectionType.SELECT_ATTR_VALUES,
        attribute_values_map: attributeValuesMap,
      }));

      // Create a new recommendation rule
      const newRule = RecommendationRuleProto.create({
        selections: selectionCriterias,
        child_step_ids: [],
      });
      
      // Get a fresh KG observation - but don't store it in the node
      const kgObservation = getKGObservationFromKGStepInterface(currentNode?.id);
      
      // Get recommendation edits from API with the fresh observation
      const recommendationEdits = await getRecommendationEdits(
        GetRecommendations_Request.create({
          kg_observation: kgObservation,
          selection_criteria: selectionCriterias[0],
        })
      );
      
      if (!recommendationEdits || !recommendationEdits.recommendations) {
        showToast("Failed to get recommendation edits", "", "error");
        setIsLoadingRecommendation(false);
        return;
      }
      // Create updated recommendations array
      const updatedRecommendations = [...currentNode?.recommendation_rules || [], newRule];
      // Create a copy of the current node to update
      const updatedNode = { ...currentNode };
      // Update the recommendation_rules in the node
      updatedNode.recommendation_rules = [...updatedRecommendations];
      // Initialize the recommendation_edits_map if it doesn't exist
      if (!updatedNode.recommendation_edits_map) {
        updatedNode.recommendation_edits_map = new Map<number, RecommendationProto[]>();
      }
      
      // Save the recommendation edits using the correct index
      updatedNode.recommendation_edits_map.set(
        currentNode?.recommendation_rules?.length || 0,
        recommendationEdits?.recommendations || []
      );
      
      // Update the node in context
      updateNode(updatedNode);
      
      // Reset the attribute selections
      setAddedAttributes([]);
      setAttributeSelection({
        column: null,
        selectedValues: [],
        attributeValues: [],
        isLoading: false,
      });
      
      // Reset selection type
      setSelectionType(SelectionType.NONE);
    } catch (error) {
      console.error("Error adding attribute selection:", error);
      showToast("Failed to add recommendation", "", "error");
    } finally {
      setIsLoadingRecommendation(false);
    }
  };
  
  // Add metrics/measures selection
  const addMetricsMeasuresSelection = async() => {
    if (selectedColumns.length === 0) return;
    
    setIsLoadingRecommendation(true);
    
    try {
      // Create column hashes
      const columnHashes = selectedColumns.map(column => 
        ColumnHashProto.create({ 
          actuals_id: column.column_id || "" 
        })
      );
      
      // Create selection criteria
      const selectionCriterias: SelectionCriteriaProto[] = [];
      selectionCriterias.push(SelectionCriteriaProto.create({
        type: SelectionType.SELECT_METRIC_MEASURES,
        criteria_columns: columnHashes,
      }));
      
      // Create a new recommendation rule
      const newRule = RecommendationRuleProto.create({
        selections: selectionCriterias,
        child_step_ids: [],
      });
      
      // Get a fresh KG observation - but don't store it in the node
      const kgObservation = getKGObservationFromKGStepInterface(currentNode?.id);
      
      // Get recommendation edits from API with the fresh observation
      const recommendationEdits = await getRecommendationEdits(
        GetRecommendations_Request.create({
          kg_observation: kgObservation,
          selection_criteria: selectionCriterias[0],
        })
      );
      
      if (!recommendationEdits || !recommendationEdits.recommendations) {
        showToast("Failed to get recommendation edits", "", "error");
        setIsLoadingRecommendation(false);
        return;
      }
      // Create updated recommendations array
      const updatedRecommendations = [...currentNode?.recommendation_rules || [], newRule];
      // Create a copy of the current node to update
      const updatedNode = { ...currentNode };
      // Update the recommendation_rules in the node
      updatedNode.recommendation_rules = [...updatedRecommendations];
      // Initialize the recommendation_edits_map if it doesn't exist
      if (!updatedNode.recommendation_edits_map) {
        updatedNode.recommendation_edits_map = new Map<number, RecommendationProto[]>();
      }
      // Save the recommendation edits using the correct index
      updatedNode.recommendation_edits_map.set(
        currentNode?.recommendation_rules?.length || 0,
        recommendationEdits?.recommendations || []
      );
      // Update the node in context
      updateNode(updatedNode);
      
      // Reset selected columns
      setSelectedColumns([]);
      // Reset selection type
      setSelectionType(SelectionType.NONE);
    } catch (error) {
      console.error("Error adding metrics/measures selection:", error);
      showToast("Failed to add recommendation", "", "error");
    } finally {
      setIsLoadingRecommendation(false);
    }
  };
  
  // Add TopN or BottomN selection
  const addTopBottomNSelection = async() => {
    if (selectedColumnHashes.length === 0 || nValue <= 0) return;
    
    setIsLoadingRecommendation(true);
    
    try {
      // Create selection criteria
      const selectionCriterias: SelectionCriteriaProto[] = [];
      selectionCriterias.push(SelectionCriteriaProto.create({
        type: selectionType as SelectionType,
        criteria_columns: selectedColumnHashes,
        threshold: nValue,
      }));
      
      // Create a new recommendation rule
      const newRule = RecommendationRuleProto.create({
        selections: selectionCriterias,
        child_step_ids: [],
      });
      
      // Get a fresh KG observation - but don't store it in the node
      const kgObservation = getKGObservationFromKGStepInterface(currentNode?.id);
      
      // Get recommendation edits from API with the fresh observation
      const recommendationEdits = await getRecommendationEdits(
        GetRecommendations_Request.create({
          kg_observation: kgObservation,
          selection_criteria: selectionCriterias[0],
        })
      );
      
      if (!recommendationEdits || !recommendationEdits.recommendations) {
        showToast("Failed to get recommendation edits", "", "error");
        setIsLoadingRecommendation(false);
        return;
      }
      
      // Create updated recommendations array
      const updatedRecommendations = [...currentNode?.recommendation_rules || [], newRule];
      // Create a copy of the current node to update
      const updatedNode = { ...currentNode };
      // Update the recommendation_rules in the node
      updatedNode.recommendation_rules = [...updatedRecommendations];
      // Initialize the recommendation_edits_map if it doesn't exist
      if (!updatedNode.recommendation_edits_map) {
        updatedNode.recommendation_edits_map = new Map<number, RecommendationProto[]>();
      }
      // Save the recommendation edits using the correct index
      updatedNode.recommendation_edits_map.set(
        currentNode?.recommendation_rules?.length || 0,
        recommendationEdits?.recommendations || []
      );
      // Update the node in context
      updateNode(updatedNode);
      // Reset selected columns
      setSelectedColumnHashes([]);
      setNValue(5);
      // Reset selection type
      setSelectionType(SelectionType.NONE);
    } catch (error) {
      console.error("Error adding Top/Bottom-N selection:", error);
      showToast("Failed to add recommendation", "", "error");
    } finally {
      setIsLoadingRecommendation(false);
    }
  };
  
  // Add select all rows rule
  const addSelectAllRowsRule = async() => {
    setIsLoadingRecommendation(true);
    
    try {
      const selectionCriterias: SelectionCriteriaProto[] = [];
      selectionCriterias.push(SelectionCriteriaProto.create({
        type: SelectionType.SELECT_ALL_ROWS,
      }));

      // Create a new recommendation rule
      const newRule = RecommendationRuleProto.create({
        selections: selectionCriterias,
        child_step_ids: [],
      });
      
      // Get a fresh KG observation - but don't store it in the node
      const kgObservation = getKGObservationFromKGStepInterface(currentNode?.id);
      
      // Get recommendation edits from API with the fresh observation
      const recommendationEdits = await getRecommendationEdits(
        GetRecommendations_Request.create({
          kg_observation: kgObservation,
          selection_criteria: selectionCriterias[0],
        })
      );
      
      if (!recommendationEdits || !recommendationEdits.recommendations) {
        showToast("Failed to get recommendation edits", "", "error");
        setIsLoadingRecommendation(false);
        return;
      }
      // Create updated recommendations array
      const updatedRecommendations = [...currentNode?.recommendation_rules || [], newRule];
      // Create a copy of the current node to update
      const updatedNode = { ...currentNode };
      // Update the recommendation_rules in the node
      updatedNode.recommendation_rules = [...updatedRecommendations];
      // Initialize the recommendation_edits_map if it doesn't exist
      if (!updatedNode.recommendation_edits_map) {
        updatedNode.recommendation_edits_map = new Map<number, RecommendationProto[]>();
      }
      // Save the recommendation edits using the correct index
      updatedNode.recommendation_edits_map.set(
        currentNode?.recommendation_rules?.length || 0,
        recommendationEdits?.recommendations || []
      );
      // Update the node in context
      updateNode(updatedNode);
      // Reset selection type
      setSelectionType(SelectionType.NONE);
    } catch (error) {
      console.error("Error adding Select All Rows rule:", error);
      showToast("Failed to add recommendation", "", "error");
    } finally {
      setIsLoadingRecommendation(false);
    }
  };
  
  // Add current selections as a recommendation
  const addRecommendation = async() => {
    switch (selectionType) {
      case SelectionType.SELECT_ALL_ROWS:
        await addSelectAllRowsRule();
        break;
      case SelectionType.SELECT_ATTR_VALUES:
        await addAttributeSelection();
        break;
      case SelectionType.SELECT_METRIC_MEASURES:
        await addMetricsMeasuresSelection();
        break;
      case SelectionType.TOP_N_PERCENT:
      case SelectionType.BOTTOM_N_PERCENT:
        await addTopBottomNSelection();
        break;
      default:
        console.error("Unsupported selection type:", selectionType);
    }
  };
  
  // Remove a recommendation
  const removeRecommendation = (index: number) => {
    if (selectedNodeId) {
      // Delete all child nodes associated with this recommendation.
      deleteChildrenOfRecommendation(selectedNodeId, index);
    }
  };
  
  // Get display text for recommendation
  const getRecommendationDisplayText = (rule: RecommendationRuleProto, columnMap: Map<string, ObservationColumnProto>): string => {
    const { selections } = rule;
    
    if (!selections) return "Unknown selection";
    const displayText = selections?.map(selection => {
      switch (selection.type) {
        case SelectionType.SELECT_ALL_ROWS:
          return "Select All Rows";
      
      case SelectionType.SELECT_ATTR_VALUES:
        return selection?.attribute_values_map?.map(attr => {
          // Try to find column name
          const columnName = findColumnNameById(attr?.column_id || "");
          if (!columnName) return `Unknown column: ${attr?.values.length} values`;
          
          // Format the values for display
          let valueDisplay = `${attr?.values.length} values`;
          if (attr?.values.length <= 3) {
            valueDisplay = attr?.values.join(", ");
          }
          
          return `${columnName}: ${valueDisplay}`;
        }).join(", ");
      
      case SelectionType.SELECT_METRIC_MEASURES:
        return selection?.criteria_columns?.map(hash => {
          // Try to find column name
          const columnName = findColumnNameById(hash?.actuals_id || "");
          return columnName || "Unknown column";
        }).filter(Boolean).join(", ") || "No resolved columns";
      
      case SelectionType.TOP_N_PERCENT:
      case SelectionType.BOTTOM_N_PERCENT:
        // Find matching column hash options for better display names
        const selectedOptions = selection?.criteria_columns?.map(hash => {
          // Look for a matching option in our columnHashOptions
          const option = columnHashOptions.find(opt => 
            opt.columnHash.actuals_id === hash.actuals_id && 
            opt.columnHash.benchmark_id === hash.benchmark_id &&
            opt.columnHash.analysis_column_type === hash.analysis_column_type
          );
          
          if (option) {
            return option.label;
          }
          
          // If option not found, try to build a name from components
          const actualName = findColumnNameById(hash?.actuals_id || "");
          if (!actualName) return null; // Skip if we can't resolve the column
          
          if (hash.benchmark_id) {
            const benchmarkName = findColumnNameById(hash.benchmark_id);
            if (!benchmarkName) return actualName; // Fall back to just actual name
            
            if (hash.analysis_column_type) {
              // Get variance type display using the getAnalysisColumnTypeDisplay utility
              const analysisType = getAnalysisColumnTypeDisplay(hash.analysis_column_type);
              return `${analysisType} of ${benchmarkName} of ${actualName}`;
            }
            
            return `${benchmarkName} of ${actualName}`;
          }
          
          return actualName;
        }).filter(Boolean).join(", ");
        
        return `${selection.type === SelectionType.TOP_N_PERCENT ? "Top" : "Bottom"} ${selection?.threshold} of: ${selectedOptions || "No resolved columns"}`;
        
      default:
        return `Selection type: ${selection?.type}`;
      }
    }).join(", ");
    return displayText;
  };
  
  // Check if Add button should be enabled
  const isAddButtonEnabled = () => {
    switch (selectionType) {
      case SelectionType.SELECT_ALL_ROWS:
        return true;
      case SelectionType.SELECT_ATTR_VALUES:
        return addedAttributes.length > 0 || 
              (attributeSelection.column !== null && attributeSelection.selectedValues.length > 0);
      case SelectionType.SELECT_METRIC_MEASURES:
        return selectedColumns.length > 0;
      case SelectionType.TOP_N_PERCENT:
      case SelectionType.BOTTOM_N_PERCENT:
        return selectedColumnHashes.length > 0 && nValue > 0;
      default:
        return false;
    }
  };
  
  // Check if "Add Attribute" button should be enabled
  const isAddAttributeEnabled = () => {
    return attributeSelection.column !== null && attributeSelection.selectedValues.length > 0;
  };
  
  // Helper function to check if a column hash is selected
  const isColumnHashSelected = (columnHash: ColumnHashProto) => {
    return selectedColumnHashes.some(ch => 
      ch?.actuals_id === columnHash?.actuals_id && 
      ch?.benchmark_id === columnHash?.benchmark_id &&
      ch?.analysis_column_type === columnHash?.analysis_column_type
    );
  };
  
  return (
    <Stack spacing={2} key={`kg-recommendations-${selectedNodeId || "no-node"}`}>
      {/* Selection Type Dropdown */}
      <FormControl>
        <Select
          value={selectionType}
          onChange={(e) =>
            setSelectionType(parseInt(e.target.value) as SelectionType)
          }
          placeholder="Select a type"
        >
          <option value={SelectionType.SELECT_ALL_ROWS}>Select All Rows</option>
          <option value={SelectionType.SELECT_ATTR_VALUES}>
            Select by Attribute Values
          </option>
          <option value={SelectionType.SELECT_METRIC_MEASURES}>
            Select Metrics/Measures
          </option>
          <option value={SelectionType.TOP_N_PERCENT}>Select Top N</option>
          <option value={SelectionType.BOTTOM_N_PERCENT}>
            Select Bottom N
          </option>
        </Select>
      </FormControl>

      {/* UI for SELECT_ATTR_VALUES */}
      {selectionType === SelectionType.SELECT_ATTR_VALUES && (
        <Box>
          {/* Show already added attributes */}
          {addedAttributes.length > 0 && (
            <Box mt={2} mb={3}>
              <Text fontWeight="medium" fontSize="sm" mb={1}>
                Added Attributes:
              </Text>
              <Stack spacing={1}>
                {addedAttributes.map((attr, index) => (
                  <Flex
                    key={index}
                    borderWidth="1px"
                    borderRadius="md"
                    p={2}
                    justifyContent="space-between"
                    alignItems="center"
                    bg="gray.50"
                  >
                    <Text fontSize="sm">
                      <b>{attr.column.kg_column_header?.name}:</b>{" "}
                      {attr.values.length} values
                    </Text>
                    <IconButton
                      aria-label="Remove attribute"
                      icon={<CloseIcon />}
                      size="xs"
                      onClick={() => removeAttributeFromSelection(index)}
                      variant="ghost"
                      colorScheme="red"
                    />
                  </Flex>
                ))}
              </Stack>
            </Box>
          )}

          <FormControl mt={2}>
            <FormLabel mb={1}>Select Attribute Column</FormLabel>
            <Menu closeOnSelect={true} key={`attr-menu-${selectedNodeId}`}>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                w="100%"
                textAlign="left"
                variant="outline"
                colorScheme="blue"
                size="sm"
                isDisabled={attributeColumns?.length === 0}
              >
                {attributeSelection.column
                  ? attributeSelection.column.kg_column_header?.name ||
                    "Unnamed Column"
                  : attributeColumns?.length === 0
                  ? "No attribute columns available"
                  : "Select a column"}
              </MenuButton>
              <Portal>
                <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
                  {attributeColumns?.map((column, index) => (
                    <MenuItem
                      key={`${selectedNodeId}-${column.column_id}-${index}`}
                      onClick={() => handleAttributeColumnSelect(column)}
                      _hover={{ bg: "blue.50" }}
                      isDisabled={addedAttributes.some(
                        (attr) => attr.column.column_id === column.column_id
                      )}
                    >
                      {column.kg_column_header?.name || "Unnamed Column"}
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          </FormControl>

          {attributeSelection.column && (
            <FormControl mt={2}>
              <FormLabel mb={1}>Select Values</FormLabel>
              {attributeSelection.isLoading ? (
                <Flex justify="center" align="center" py={4}>
                  <Spinner size="sm" mr={2} />
                  <Text>Loading values...</Text>
                </Flex>
              ) : (
                <ColumnValues
                  contextValues={attributeSelection.attributeValues}
                  selectedOptions={attributeSelection.selectedValues}
                  setSelectedOptions={handleAttributeValuesChange}
                />
              )}
            </FormControl>
          )}

          {/* Add Attribute Button */}
          {attributeSelection.column &&
            attributeSelection.selectedValues.length > 0 && (
              <Button
                mt={2}
                size="sm"
                colorScheme="teal"
                onClick={addAttributeToSelection}
                isDisabled={!isAddAttributeEnabled()}
                leftIcon={<AddIcon />}
              >
                Add Attribute
              </Button>
            )}
        </Box>
      )}

      {/* UI for SELECT_METRIC_MEASURES */}
      {selectionType === SelectionType.SELECT_METRIC_MEASURES && (
        <FormControl mt={2}>
          <FormLabel mb={1}>Select Metrics/Measures</FormLabel>
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            p={2}
            maxH="200px"
            overflowY="auto"
            key={`metrics-box-${selectedNodeId}`}
          >
            <Stack spacing={1}>
              {nonAttributeColumns?.map((column, index) => (
                <Checkbox
                  key={`${selectedNodeId}-metric-${column.column_id}-${index}`}
                  isChecked={selectedColumns.some(
                    (c) => c.column_id === column.column_id
                  )}
                  onChange={() => toggleColumnSelection(column)}
                  colorScheme="blue"
                >
                  {column.kg_column_header?.name || "Unnamed Column"}
                </Checkbox>
              ))}
            </Stack>
          </Box>
        </FormControl>
      )}

      {/* UI for TOP_N and BOTTOM_N */}
      {(selectionType === SelectionType.TOP_N_PERCENT ||
        selectionType === SelectionType.BOTTOM_N_PERCENT) && (
        <Box>
          <FormControl mt={2}>
            <FormLabel>
              {selectionType === SelectionType.TOP_N_PERCENT
                ? "Top N Value"
                : "Bottom N Value"}
            </FormLabel>
            <NumberInput
              min={1}
              max={100}
              value={nValue}
              onChange={(valueString) => setNValue(parseInt(valueString))}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl mt={3}>
            <FormLabel mb={1}>Select Columns for Analysis</FormLabel>
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={2}
              maxH="300px"
              overflowY="auto"
              key={`hash-options-${selectedNodeId}`}
            >
              <Stack spacing={2}>
                {columnHashOptions?.map((option, index) => (
                  <Checkbox
                    key={`${selectedNodeId}-hash-${option.columnHash.actuals_id}-${option.columnHash.benchmark_id}-${option.columnHash.analysis_column_type}-${index}`}
                    isChecked={isColumnHashSelected(option.columnHash)}
                    onChange={() =>
                      toggleColumnHashSelection(option.columnHash)
                    }
                    colorScheme="blue"
                  >
                    {option.label}
                  </Checkbox>
                ))}
              </Stack>
            </Box>
          </FormControl>
        </Box>
      )}

      {/* Add Selection Button */}
      {selectionType !== SelectionType.NONE && (
        <Button
          mt={2}
          colorScheme="blue"
          onClick={addRecommendation}
          isDisabled={!isAddButtonEnabled() || isLoadingRecommendation}
          leftIcon={isLoadingRecommendation ? <Spinner size="sm" /> : <AddIcon />}
          size="sm"
        >
          {isLoadingRecommendation ? "Adding..." : "Add Recommendation"}
        </Button>
      )}

      {/* Display Added Recommendations */}
      {currentNode?.recommendation_rules?.length > 0 && (
        <Box mt={0}>
          <Text fontWeight="medium" mb={2}>
            Added Recommendations:
          </Text>
          <Stack spacing={1}>
            {currentNode?.recommendation_rules?.map((rule, index) => (
              <Box
                key={index}
                borderWidth="1px"
                borderRadius="md"
                p={2}
                borderColor="gray.200"
              >
                <Flex
                  justifyContent="space-between"
                  alignItems="flex-start"
                  mb={2}
                >
                  <Box maxW="80%" overflow="hidden">
                    <Text fontWeight="bold" fontSize="sm" mb={1} isTruncated>
                      Recommendation {index + 1}
                    </Text>
                    <Badge
                      colorScheme="blue"
                      mb={2}
                      maxW="100%"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {getRecommendationDisplayText(rule, columnMap)}
                    </Badge>
                  </Box>
                  <IconButton
                    aria-label="Remove recommendation"
                    icon={<CloseIcon />}
                    size="xs"
                    onClick={() => removeRecommendation(index)}
                    variant="ghost"
                    colorScheme="red"
                    ml={2}
                    flexShrink={0}
                  />
                </Flex>

                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={() => handleAddRecommendationAndChild(index)}
                  leftIcon={loadingChildIndex === index ? <Spinner size="xs" /> : <AddIcon />}
                  isDisabled={loadingChildIndex !== null}
                >
                  {loadingChildIndex === index ? "Creating Child..." : "Add Child Node"}
                </Button>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default KGRecommendations; 